import { FC, MouseEvent, useEffect, useState } from "react";
import { ContactUs } from "./ContactUs";
import { Button } from "../ui/Button/Button";
import { EndScreen } from "../EndScreen/EndScreen";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as z from "zod";

const contactSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email format"),
  phone: z.string().regex(/^\+?[1-9]\d{9,14}$/, "Invalid phone number format"),
  message: z.string().min(1, "Message is required"),
});

interface Contact {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const Footer: FC = () => {
  const [isAnimate, setIsAnimate] = useState(false);
  const [contactObj, setContactObj] = useState<Contact>({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState<Contact>({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const validateForm = () => {
    const result = contactSchema.safeParse(contactObj);

    if (!result.success) {
      const newErrors: Contact = {
        name: "",
        email: "",
        phone: "",
        message: "",
      };

      result.error.issues.forEach((issue: z.ZodIssue) => {
        const field = issue.path[0] as keyof Contact;
        if (field in newErrors) {
          newErrors[field] = issue.message;
        }
      });

      setErrors(newErrors);
      return false;
    }

    setErrors({
      name: "",
      email: "",
      phone: "",
      message: "",
    });
    return true;
  };

  const resetForm = () => {
    setContactObj({
      name: "",
      email: "",
      phone: "",
      message: "",
    });
    setErrors({
      name: "",
      email: "",
      phone: "",
      message: "",
    });
  };

  const handleSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await axios.post(
        "https://b1.tagg.live/emotion-design/api/post-contact",
        contactObj,
      );

      // Always show toast and reset form on successful response
      toast.success("Successfully Submitted! ", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
      });
      resetForm();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error.response?.data?.message || "Server error. Please try again.",
          {
            position: "top-right",
            autoClose: 5000,
            theme: "dark",
          },
        );
      } else {
        toast.error("Server error. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          theme: "dark",
        });
      }
      console.error("Error submitting form:", error);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Check if the user has scrolled to the bottom of the page
      if (scrollPosition + windowHeight >= documentHeight - 100) {
        setIsAnimate(true);
      } else {
        setIsAnimate(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="w-screen text-white flex h-[200vh] md:h-[130vh] relative -mt-16">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        style={{ zIndex: 9999 }}
      />
      <div className="w-full mx-auto px-2 md:px-4 sticky top-0 h-screen flex flex-col-reverse md:flex-row justify-start md:justify-center items-center overflow-hidden pt-12 md:pt-0">
        {/* Left Section */}
        <div className="w-full md:w-[50%] h-full justify-center px-4 md:pl-10">
          <ContactUs />
        </div>

        {/* Divider Line */}
        <div
          className={`hidden md:block w-0 absolute outline outline-offset-[-0.50px] transition-all duration-800 ease-in-out outline-stone-50  ${
            isAnimate
              ? "h-0 top-[50vh] origin-center"
              : "h-[80vh] top-[20vh] origin-center"
          }`}
        ></div>

        {/* Right Section - 40% width */}
        <div className="w-full md:w-[40%] px-4 md:pl-12 mt-8 md:mt-0">
          <form className="space-y-4 md:space-y-10">
            <div className="relative h-[70px]">
              <p className="text-white text-xs md:text-sm mb-1 font-[gilroy-medium]">
                Name
              </p>
              <input
                type="text"
                placeholder="Enter your full name here"
                className="w-full bg-transparent border-b border-white/20 py-2 text-sm md:text-base focus:outline-none focus:border-white transition-colors"
                value={contactObj.name}
                onChange={(e) =>
                  setContactObj((prevObj) => ({
                    ...prevObj,
                    name: e.target.value,
                  }))
                }
              />
              {errors.name && (
                <p className="absolute -bottom-5 left-0 text-red-500 text-sm">
                  {errors.name}
                </p>
              )}
            </div>
            <div className="relative h-[70px]">
              <p className="text-white text-xs md:text-sm mb-1 font-[gilroy-medium]">
                Phone Number
              </p>
              <input
                type="tel"
                placeholder="+91"
                className="w-full bg-transparent border-b border-white/20 py-2 text-sm md:text-base focus:outline-none focus:border-white transition-colors"
                maxLength={10}
                value={contactObj.phone}
                onChange={(e) =>
                  setContactObj((prevObj) => ({
                    ...prevObj,
                    phone: e.target.value,
                  }))
                }
              />
              {errors.phone && (
                <p className="absolute -bottom-5 left-0 text-red-500 text-sm">
                  {errors.phone}
                </p>
              )}
            </div>
            <div className="relative h-[70px]">
              <p className="text-white text-xs md:text-sm mb-1 font-[gilroy-medium]">
                Email
              </p>
              <input
                type="email"
                placeholder="Enter your email here"
                className="w-full bg-transparent border-b border-white/20 py-2 text-sm md:text-base focus:outline-none focus:border-white transition-colors"
                value={contactObj.email}
                onChange={(e) =>
                  setContactObj((prevObj) => ({
                    ...prevObj,
                    email: e.target.value,
                  }))
                }
              />
              {errors.email && (
                <p className="absolute -bottom-5 left-0 text-red-500 text-sm">
                  {errors.email}
                </p>
              )}
            </div>
            <div className="relative h-[70px]">
              <p className="text-white text-xs md:text-sm mb-1 font-[gilroy-medium]">
                Message
              </p>
              <input
                type="text"
                placeholder="Tell us your story"
                className="w-full bg-transparent border-b border-white/20 py-2 text-sm md:text-base focus:outline-none focus:border-white transition-colors"
                value={contactObj.message}
                onChange={(e) =>
                  setContactObj((prevObj) => ({
                    ...prevObj,
                    message: e.target.value,
                  }))
                }
              />
              {errors.message && (
                <p className="absolute -bottom-5 left-0 text-red-500 text-sm">
                  {errors.message}
                </p>
              )}
            </div>
            <Button onClick={handleSubmit}>Send</Button>
          </form>
        </div>

        <EndScreen isAnimated={isAnimate} />
      </div>
    </div>
  );
};

export default Footer;
